import { Observable } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { AppSettingsService } from './app-settings.service';
import { GroupContextService } from './group-context.service';
import { FeatureToggleService } from './feature-toggle.service';
import { hd } from './helpdesk.typings';
import { Injectable } from '@angular/core';
import { HttpService as XSightHttpService } from './http.service';

type IReporterGroupConfig = hd.IReportGroupConfig;

export interface IReporterGroupConfiguration {
  groupId?: number;
  isLdap: boolean;
  reporterGroup: string;
}

export interface IReporterModel {
  reporterId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  fullName?: string;
  isEditable?: boolean;
  kind: string;
}

@Injectable({
  providedIn: 'root',
})
export class ReporterService {
  private readonly GROUP_HEADER_ID = 'hd-group-id';

  constructor(
    private http: XSightHttpService,
    private appSettingsService: AppSettingsService,
    private groupContextService: GroupContextService,
    private featureToggleService: FeatureToggleService
  ) {}

  public searchReporters = (
    searchText: string,
    excludeIds?: string[],
  ): Observable<IReporterModel[]> => {
    let headers = this.addGroupHeaders(new HttpHeaders());
    const options = {
      headers: headers,
      params: { searchText: searchText },
    };

    if (excludeIds && excludeIds.length > 0) {
      options.params['excludePublicIds'] = excludeIds.join(',');
    }

    return this.http.get<IReporterModel[]>(
      this.appSettingsService.reporterServiceUrl + '?searchExternal=true',
      options
    );
  };

  public getManualReporters = (): Observable<IReporterModel[]> => {
    return this.http.get<IReporterModel[]>(
      this.appSettingsService.ticketServiceUrl + 'manualReporters',
    );
  }

  public getReporterGroupConfig = (): Observable<IReporterGroupConfiguration> => {
    return this.http.get<IReporterGroupConfiguration>(
      this.appSettingsService.reporterServiceUrl + 'config/reportergroup'
    );
  };

  public updateReporterGroupConfig = (
    objReportergroup: IReporterGroupConfiguration
  ): Observable<IReporterGroupConfiguration> => {
    return this.http.put<IReporterGroupConfiguration>(
      this.appSettingsService.reporterServiceUrl + 'config/reportergroup',
      objReportergroup
    );
  };

  public getReportersResource = (userId: string): Observable<IReporterModel> => {
    return this.http.get<IReporterModel>(
      this.appSettingsService.reporterServiceUrl + userId + '?searchExternal=true'
    );
  };

  public getReporterSearchResource = (reporterId: string): Observable<IReporterModel> => {
    return this.http.get<IReporterModel>(
      this.appSettingsService.reporterServiceUrl +
        ':reporterId?searchExternal=true&includeSavedReporters=true' +
        reporterId
    );
  };

  public getReporterGroupConfigResource = (): Observable<IReporterGroupConfig> => {
    return this.http.put<IReporterGroupConfig>(
      this.appSettingsService.reporterServiceUrl + 'config/reportergroup',
      null
    );
  };

  /** Add Reporter */
  public addReporter(value: IReporterModel) {
    const options = {
      headers: this.addGroupHeaders(new HttpHeaders()),
    };
    return this.http.post<IReporterModel>(this.appSettingsService.ticketServiceUrl + 'reporter', value, options);
  }

  /** Update Reporter */
  public updateReporter(ticketId: string, value: IReporterModel) {
    return this.http.put<IReporterModel>(this.appSettingsService.ticketServiceUrl + `${ticketId}/reporter/update`, value);
  }

  /**
   * Sets group id header.
   * @param headers HttpHeaders to append to
   * @return A new HttpHeaders object with the group id set
   */
  private addGroupHeaders(headers: HttpHeaders): HttpHeaders {
    if (this.featureToggleService.isEnabled('Groups')) {
      const groupId = this.groupContextService.currentGroupId;
      if (groupId && !headers.has(this.GROUP_HEADER_ID)) {
        headers = headers.set(this.GROUP_HEADER_ID, groupId.toString());
        headers.get(this.GROUP_HEADER_ID);
      }
    }
    return headers;
  }
}
